import { FormEvent } from 'react';
import { PasswordInputRow } from './PasswordInputRow';
import { MAIN_PORTAL_LOGIN_TYPE } from '../login/authEntryPolicy';
import { useLoginForm } from '../login/useLoginForm';
import { useI18n } from '../i18n';
import directionStyles from './formInputDirection.module.css';
import styles from '../pages/AuthLoginPortal.module.css';

export type AuthPortalPasswordSectionProps = {
  onForgotPassword?: () => void;
};

export function AuthPortalPasswordSection({ onForgotPassword }: AuthPortalPasswordSectionProps) {
  const { t } = useI18n();
  const {
    isLoggedIn,
    loginBlockedTitle,
    payload,
    setPayload,
    submitting,
    loginErrorFriendly,
    loginFieldKey,
    onSubmit,
  } = useLoginForm({ portalMode: true, fixedLoginOption: MAIN_PORTAL_LOGIN_TYPE });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    void onSubmit(event);
  };

  return (
    <section aria-labelledby="auth-portal-password-title">
      <p id="auth-portal-password-title" className={styles.hint}>
        {t('auth.portal.passwordHint')}
      </p>

      <form className={styles.credentialsForm} onSubmit={handleSubmit} autoComplete="off">
        <div className={styles.credField}>
          <label className={styles.credLabel} htmlFor="portal-password-email">
            {t('auth.portal.emailLabel')}
          </label>
          <input
            key={`portal-password-email-${loginFieldKey}`}
            id="portal-password-email"
            className={`${styles.credInput} ${directionStyles.inputLtr}`}
            type="email"
            inputMode="email"
            dir="ltr"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={payload.identifier}
            disabled={isLoggedIn || submitting}
            title={isLoggedIn ? loginBlockedTitle : undefined}
            placeholder={t('auth.portal.emailPlaceholder')}
            onChange={(event) =>
              setPayload((current) => ({ ...current, identifier: event.target.value }))
            }
          />
        </div>

        <div className={styles.credField}>
          <label className={styles.credLabel} htmlFor="portal-password">
            {t('login.password')}
          </label>
          <PasswordInputRow
            id="portal-password"
            inputKey={loginFieldKey}
            value={payload.password}
            onChange={(password) => setPayload((current) => ({ ...current, password }))}
            inputClassName={styles.credInput}
            toggleClassName={styles.passwordToggle}
            autoComplete="current-password"
            disabled={isLoggedIn || submitting}
            forceLtr
          />
        </div>

        {!isLoggedIn && onForgotPassword ? (
          <button type="button" className={styles.passwordFallbackLink} onClick={onForgotPassword}>
            {t('auth.forgotPassword.link')}
          </button>
        ) : null}

        {loginErrorFriendly ? (
          <div className={styles.failureBlock} role="alert">
            <p className={styles.friendlyError}>{loginErrorFriendly}</p>
          </div>
        ) : null}

        <button
          type="submit"
          className={styles.submitBtn}
          disabled={submitting || isLoggedIn}
          title={isLoggedIn ? loginBlockedTitle : undefined}
        >
          {submitting ? t('common.submitting') : t('login.login')}
        </button>
      </form>
    </section>
  );
}
